import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { useHaptic } from '../../hooks/useHaptic';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'accent' | 'danger';
  isLoading?: boolean;
  isErrorShake?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant = 'primary', className = '', isLoading = false, isErrorShake = false, type = 'button', onClick, ...props }, ref) => {
    const { lightTap, invalidAlert } = useHaptic();

    let variantStyles = '';

    switch (variant) {
      case 'primary':
        variantStyles = 'bg-primary text-white hover:bg-slate-900 active:bg-black border border-transparent shadow-sm focus:ring-2 focus:ring-black/10';
        break;
      case 'secondary':
        variantStyles = 'bg-slate-100 text-slate-800 hover:bg-slate-200 active:bg-slate-100 border border-transparent focus:ring-2 focus:ring-slate-200';
        break;
      case 'outline':
        variantStyles = 'bg-transparent border border-slate-200 text-slate-750 hover:bg-slate-50 hover:text-slate-950 focus:ring-2 focus:ring-slate-100';
        break;
      case 'accent':
        variantStyles = 'bg-accent text-white hover:bg-accent-hover active:bg-accent-active border border-transparent shadow-sm focus:ring-2 focus:ring-accent/20';
        break;
      case 'danger':
        variantStyles = 'bg-rose-500 text-white hover:bg-rose-600 active:bg-rose-700 border border-transparent shadow-sm focus:ring-2 focus:ring-rose-500/20';
        break;
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) return;
      if (isErrorShake) {
        invalidAlert();
      } else {
        lightTap();
      }
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <motion.button
        ref={ref}
        type={type}
        whileTap={{ scale: props.disabled || isLoading ? 1 : 0.98 }}
        animate={isErrorShake ? {
          x: [0, -6, 6, -6, 6, 0],
          transition: { duration: 0.4 }
        } : {}}
        onClick={handleClick}
        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-smooth focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 ${variantStyles} ${className}`}
        {...props as any}
      >
        {isLoading && (
          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
        )}
        {children}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';
